import React, { useState } from 'react';
import { contactAPI } from '../utils/api';
import SEO, { SITE_URL } from '../components/SEO';
import { toast } from 'sonner';
import { Phone, Mail, MapPin } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await contactAPI.submit(formData);
      toast.success('Thank you for contacting us! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      <SEO title="Contact Us" description="Get in touch with Studio Crochet. Visit us in Noida or call 9999633121. WhatsApp available for order queries and custom requests." canonical={`${SITE_URL}/contact`} />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-6xl leading-none mb-12 text-center" data-testid="contact-heading">Get in Touch</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="font-cormorant text-2xl sm:text-3xl mb-6">Contact Information</h2>
            <div className="space-y-6" data-testid="contact-info">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-[#C29B57] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] mb-2">Address</h3>
                  <p className="font-outfit text-base text-[#4A463F]">04, 1st floor, bliss height, sector 104, hazipur, noida-201304</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-[#C29B57] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] mb-2">Phone</h3>
                  <a href="tel:9999633121" className="font-outfit text-base text-[#4A463F] hover:text-[#C29B57] transition-colors" data-testid="contact-phone">9999633121</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 text-[#C29B57] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] mb-2">Email</h3>
                  <a href="mailto:ekta.verma320@gmail.com" className="font-outfit text-base text-[#4A463F] hover:text-[#C29B57] transition-colors" data-testid="contact-email">ekta.verma320@gmail.com</a>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h3 className="font-outfit text-sm uppercase tracking-wider text-[#1A1A18] mb-4">Business Hours</h3>
              <p className="font-outfit text-base text-[#4A463F]">Monday - Saturday: 10:00 AM - 7:00 PM</p>
              <p className="font-outfit text-base text-[#4A463F]">Sunday: By Appointment Only</p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="font-cormorant text-2xl sm:text-3xl mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base placeholder:text-[#8C8577] transition-colors outline-none"
                  data-testid="name-input"
                />
              </div>

              <div>
                <input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base placeholder:text-[#8C8577] transition-colors outline-none"
                  data-testid="email-input"
                />
              </div>

              <div>
                <input
                  type="tel"
                  placeholder="Your Phone (Optional)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base placeholder:text-[#8C8577] transition-colors outline-none"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <textarea
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows="6"
                  className="w-full border-b border-[#EBE4D5] bg-transparent rounded-none px-0 py-2 focus:ring-0 focus:border-[#C29B57] font-outfit text-base placeholder:text-[#8C8577] transition-colors outline-none resize-none"
                  data-testid="message-input"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-8 py-4 font-outfit uppercase tracking-wider text-sm disabled:opacity-50"
                data-testid="submit-button"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;